import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CalendarButton } from './CalendarButton'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { CustomActionBar } from './CustomActionBar'
import { DatePickerType } from './typings'
import { ENTITY_TYPES } from '@sales-center/common'

export type CalendarDatePickerProps = {
  value: Date | null
  onAccept: (date: Date | null) => void
  disabled?: boolean
  canUpdate?: boolean
  clearable?: boolean
  disablePast?: boolean
  disableFuture?: boolean
  datePickerType?: DatePickerType
  entityType?: ENTITY_TYPES
  updateOtherDateCallback?: (update: any) => void
  id?: number
  format?: string
  tidal?: boolean
  textOnly?: boolean
  color?: string
  dataTestId?: string
}

export function CalendarDatePicker(props: CalendarDatePickerProps) {
  const {
    value,
    canUpdate,
    clearable,
    datePickerType = 'none',
    entityType,
    updateOtherDateCallback,
    onAccept,
    id,
    format,
    color,
    disabled,
    dataTestId,
    textOnly = false,
  } = props

  const [open, setOpen] = useState(false)

  const [updateOtherDate, setUpdateOtherDate] = useState<boolean>(false)
  const onOpen = useCallback(() => setOpen(true), [])
  const onClose = useCallback(() => setOpen(false), [])

  const [nextActionDate, setNextActionDate] = useState<Date | null>(value)

  const onChange = useCallback((selection: Date | null) => {
    setNextActionDate(selection)
  }, [])
  const originalNextActionDate = useMemo(() => value, [value])
  useEffect(() => {
    if (value !== originalNextActionDate) {
      setNextActionDate(value)
    }
  }, [originalNextActionDate, value])

  const onCancelDialog = useCallback(() => {
    onClose()
    setUpdateOtherDate(false)
    setNextActionDate(originalNextActionDate)
  }, [onClose, originalNextActionDate])

  const onClearDialog = useCallback(() => {
    onClose()
    setUpdateOtherDate(false)
    onAccept(null)
  }, [onAccept, onClose])

  const onAcceptDialog = useCallback(() => {
    onClose()
    if (originalNextActionDate !== nextActionDate) {
      onAccept(nextActionDate)
    }
    if (
      updateOtherDate &&
      updateOtherDateCallback &&
      datePickerType !== 'none'
    ) {
      if (datePickerType === 'nextaction') {
        updateOtherDateCallback({
          opportunityId: id,
          expectedClosingDate: nextActionDate,
        })
      } else {
        updateOtherDateCallback({
          opportunityId: id,
          nextActionDate: nextActionDate,
        })
      }
      setUpdateOtherDate(false)
    }
  }, [
    nextActionDate,
    onAccept,
    onClose,
    originalNextActionDate,
    updateOtherDate,
    updateOtherDateCallback,
    id,
    datePickerType,
  ])

  const onCloseDialog = useCallback((_event: object, reason: string) => {
    if (reason == 'backdropClick') {
      return
    }
  }, [])

  return (
    <MobileDatePicker
      {...props}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      onChange={onChange}
      showToolbar={false}
      format={format}
      value={nextActionDate}
      DialogProps={{
        onClose: onCloseDialog,
      }}
      slots={{
        toolbar: undefined,
        actionBar: canUpdate ? CustomActionBar : undefined,
        textField: () => (
          <CalendarButton
            date={value}
            onClick={onOpen}
            formatString={format}
            color={color}
            disabled={disabled}
            dataTestId={dataTestId}
            textOnly={textOnly}
          />
        ),
      }}
      slotProps={{
        actionBar: {
          /*eslint-disable */
          //@ts-ignore
          isnextactiondate: datePickerType,
          setupdateotherdate: setUpdateOtherDate,
          onCancel: onCancelDialog,
          onAccept: onAcceptDialog,
          onClear: onClearDialog,
          disabled: !nextActionDate,
          entitytype: entityType,
          actions: clearable
            ? ['clear', 'cancel', 'accept']
            : ['cancel', 'accept'],
        },
      }}
    />
  )
}
